import React, { useCallback, useMemo } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquareText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  getMediaDetailedViewHref,
  mediaDetailedViewsColumns,
} from "./tableParams/mediaDetailedViewsColumns";
import StatsAPI from "../../../api/StatsAPI";
import { API_DATE_FORMAT } from "../../../constants/dateFormat";
import useAbility from "../../../hooks/useAbility";
import { navigatorLanguage } from "../../../i18n";
import { getElementByUuid, getLanguageUuid } from "../../../utils/data/Content";
import { downloadFile } from "../../../utils/file";
import DataTable from "../../dataTable/DataTable";
import { Button } from "../../ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import ExportCsvButton from "../charts/ExportCsvButton";

import type { JSX } from "react";

import type { GetMediasItem } from "@/src/api/MediaAPI.types";
import type { AnalyticsPeriod } from "@/src/hooks/usePeriodFilter";

import type { MediaDetailedView } from "../../../api/StatsAPI.types";
import type { AppState } from "../../../reducers";

interface MediaDetailedViewsTableProps {
  contentId: string;
  period: AnalyticsPeriod;
}

function MediaDetailedViewsTable({
  contentId,
  period,
}: MediaDetailedViewsTableProps): JSX.Element {
  const { projectSlug, id } = useParams<{ projectSlug: string; id: string }>();
  const { t } = useTranslation("editor-analytics");
  const { canAccess } = useAbility();
  const queryClient = useQueryClient();

  const languages = useSelector(
    (state: AppState) => state.fetchReducer.languages,
  );
  const orgaConfig = useSelector(
    (state: AppState) => state.fetchReducer.orga_config,
  );
  const content = useSelector(
    (state: AppState) =>
      getElementByUuid(id, state.fetchReducer.contents) as GetMediasItem,
  );

  const { language_uuid: languageUuid } = getLanguageUuid(
    languages,
    navigatorLanguage,
  );
  const { project: canAccessProject } = canAccess(
    orgaConfig.data?.organization_options,
  );
  const canAccessUsers = canAccessProject.usersAnalytics;

  const getLiveChatQuery = StatsAPI.generateQuery({
    fn: StatsAPI.getLiveChat,
    params: [
      {
        projectSlug,
        mediaUuid: id,
        isExport: true,
      },
    ],
  });

  const queryParams = {
    projectSlug,
    mediaUuid: contentId,
    startDate: period.startDate.format(API_DATE_FORMAT),
    endDate: period.endDate.format(API_DATE_FORMAT),
    languageUuid,
  };

  const getMediaDetailedViewsQuery = StatsAPI.generateQuery({
    fn: StatsAPI.getMediaDetailedViews,
    params: [queryParams],
  });

  const {
    data: mediaDetailedViews,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    ...getMediaDetailedViewsQuery,
    enabled: !!contentId,
  });

  const viewList = useMemo(
    (): MediaDetailedView[] => mediaDetailedViews?.data ?? [],
    [mediaDetailedViews?.data],
  );

  const columnList = useMemo(
    () => mediaDetailedViewsColumns({ t, projectSlug, canAccessUsers }),
    [t, projectSlug, canAccessUsers],
  );

  const getRowHref = useCallback(
    (view: MediaDetailedView): string | undefined =>
      getMediaDetailedViewHref(view, projectSlug, canAccessUsers),
    [projectSlug, canAccessUsers],
  );

  // Toutes les sessions, pas seulement les lignes filtrées.
  const canExport = !isLoading && viewList.length > 0;

  const fetchExport = async (): Promise<string> =>
    StatsAPI.getMediaDetailedViews({ ...queryParams, isExport: true });

  const handleClickExportLiveChatDiscussion = async (): Promise<void> => {
    try {
      const data = await queryClient.fetchQuery(getLiveChatQuery);
      downloadFile({
        payload: new Blob([data]),
        filename: `export_chat_${id}`,
        fileExtension: "csv",
      });
    } catch (error) {
      toast.error(t("genericError", { ns: "error" }));
      console.error(error);
    }
  };

  const isLive = content?.type === "live";

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle>{t("contentItemAnalyticsTitle")}</CardTitle>
        <CardAction className="flex items-center gap-2">
          {isLive ? (
            <Button
              onClick={handleClickExportLiveChatDiscussion}
              type="button"
              variant="ghost"
            >
              <MessageSquareText aria-hidden />
              {t("download_livechat")}
            </Button>
          ) : null}
          <ExportCsvButton
            disabled={!canExport}
            fetchCsv={fetchExport}
            fileName="media_detailed_views.csv"
          />
        </CardAction>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columnList}
          data={viewList}
          defaultSorting={{ id: "inserted_at", desc: true }}
          emptyText={t("noDatas")}
          getRowHref={getRowHref}
          isError={isError}
          isLoading={isLoading}
          onRetry={refetch}
          resetPageOn={[period.startDate.valueOf(), period.endDate.valueOf()]}
        />
      </CardContent>
    </Card>
  );
}

export default MediaDetailedViewsTable;
